import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { Switch } from "@/components/ui/switch";
import { SPORTS, fmtDate } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const recipeLabel = (r) => { try { return `Prototype: ${JSON.parse(r).label || "custom"}`; } catch { return "Prototype"; } };
const describe = (p) => [p.sport && SPORTS[p.sport]?.label, p.recipe && recipeLabel(p.recipe), p.attrs?.split(",").map((k) => k.replace(/-/g, " ")).join(" + "),
  p.division, p.position_group, p.position, p.team, p.class_year, p.q && `"${p.q}"`].filter(Boolean).join(" · ");

export default function SavedSearchesPage() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/saved-searches").then((r) => setItems(r.data.items)), []);
  useEffect(() => { load(); }, [load]);
  const patch = async (s, body) => {
    try { await api.patch(`/saved-searches/${s.id}`, body); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    try { await api.delete(`/saved-searches/${s.id}`); toast.success(`Deleted "${s.name}"`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <AppHeader />
      <main className="mx-auto max-w-[1000px] px-4 pb-24 pt-10 sm:px-8">
        <p className="eyebrow !text-[#FF2D78]">Saved searches</p>
        <h1 className="mt-3 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl lg:text-6xl">Your watchlist.</h1>
        <p className="mt-4 max-w-xl text-sm text-slate-400">We check these after every data update and once a day, and alert you when new players match.</p>
        {items && !items.length && (
          <div data-testid="saved-empty" className="mt-10 rounded-xl border border-dashed border-slate-800 p-12 text-center text-sm text-slate-400">
            No saved searches yet. Filter the <Link to="/players" className="text-[#FF2D78]">player database</Link> and press Save search.
          </div>
        )}
        <div className="mt-10 space-y-3">
          {items?.map((s) => (
            <div key={s.id} data-testid={`saved-search-${s.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-800 bg-[#131927] p-5">
              <Link to={`/${s.params.recipe ? "prototypes" : "players"}?${new URLSearchParams(s.params)}`} data-testid={`saved-search-open-${s.id}`} className="min-w-0 flex-1">
                <div className="font-semibold text-white hover:text-[#FF2D78]">{s.name}</div>
                <div className="mt-1 truncate text-xs capitalize text-slate-400">{describe(s.params)}</div>
                <div className="mt-1 font-data text-[10px] text-slate-500">{s.match_count} matches · checked {fmtDate(s.last_checked_at)}</div>
              </Link>
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <Switch data-testid={`saved-search-email-${s.id}`} checked={s.email_alerts} onCheckedChange={(v) => patch(s, { email_alerts: v })} /> Email alerts
              </label>
              <button data-testid={`saved-search-delete-${s.id}`} onClick={() => remove(s)} aria-label="Delete"
                className="rounded-full border border-slate-700 p-2 text-slate-400 transition-colors duration-200 hover:border-red-400 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
